import { abGetDefaultCity } from '../ab/widgets-home';
import { YPaymentAmount } from '../y/amount';
import { makeDataStore } from '../y/data-store';
import { $fn } from '../y/fn';
import { $http } from '../y/http';
import { $i18n, yGetI18N, YI18NString, YLanguage } from '../y/i18n';
import { yCopy } from '../y/object';
import { $time, YTime } from '../y/time';
import { $id, YCountryId, YId, YMaybeObjectId, YObjectId, YTimeString } from '../y/types';
import { CReviewHomeView } from './c-booking';
import { c_cities, CCity, cCityBySlug } from './c-city';
import { c_event_tags, CEventTag, cEventTagName } from './c-event-tag';
import { c_producers, CProducer } from './c-producer';
import { c_venues, CVenueSceneView } from './c-venue';

export type LegacyCantBookReason = 'cancelled' | 'no_subs' | 'cancel_subs' | 'paused_subs' | 'sales_ended' | 'event_booking' | 'past_booking' | 'day_booking' | 'two_bookings' | 'no_tickets' | 'past_due' | 'loyalty' | 'loyalty_paused' | 'loyalty_inactive' | 'expired';

interface LegacyEventLinks {
    youtube: string;
    vimeo: string;
    spotify: string;
    soundcloud: string;
}

export type LegacyEventRestriction = 'loyalty' | 'no_booking_limit' | 'no_same_day' | 'no_same_event' | 'bookable_early';

export interface LegacyEventSession {
    id: YObjectId;
    drop_in: boolean;
    time: YTimeString;
    end_time: YTimeString;
    cancelled: boolean;
    sales_end: number;
    length: number;

    amount_booked: number; // TODO: move to admin
    is_bookable_early?: boolean; // TODO: move to home
    restrictions?: LegacyEventRestriction[]; // TODO: move to admin

    //dynamic
    time_y: YTime;
    is_loyalty: boolean;
}

export interface LegacyEventVideo {
    id: YObjectId;
    vod: YMaybeObjectId;
    stream: YMaybeObjectId;
    date: YTimeString;
    expires: YTimeString;
    is_ppv: boolean;
    is_open: boolean;
    is_member_open: boolean;
    gone: boolean;
    cancelled: boolean;
    duration: number;

    availability?: YCountryId[];
}

export interface LegacyEventModel {
    id: YObjectId;
    owner: YObjectId; // TODO: move to admin and also move duplicateModel

    producer: YObjectId;
    tags: YObjectId[];
    keywords: string[];

    city: YObjectId;
    venue: YMaybeObjectId;
    scene: YMaybeObjectId;
    show_scene: boolean;

    slug: string;
    names: YI18NString;
    descriptions: YI18NString;
    links: LegacyEventLinks;

    pictures: YId[];
    videos: LegacyEventVideo[];
    sessions: LegacyEventSession[];

    length: number;
    published: boolean;

    ratings_up: number;
    ratings_down: number;

    published_at: YTimeString;

    hide_in_search: boolean;
    dots: number;
    popularity: number;
}

export function legacyIsSessionBookable(s: LegacyEventSession, future?: boolean) {
    const now = $time.now();
    return now.add(s.sales_end, $time.hour).before(s.time_y) && (future || s.restrictions?.includes('bookable_early') || s.is_bookable_early || s.time_y.before(now.add(49, $time.day)));
}

export function legacyHasSalesEnded(s: LegacyEventSession) {
    const now = $time.now();
    return now.add(s.sales_end, $time.hour).after(s.time_y);
}

export function legacyIsSessionPast(s: LegacyEventSession) {
    const now = $time.now();
    return now.add(s.sales_end, $time.hour).after(s.time_y);
}

export class LegacyEvent {
    model!: LegacyEventModel;

    private _search?: string;
    private _sessions!: LegacyEventSession[];

    constructor(model: LegacyEventModel) {
        this.updateModel(model);
    }

    updateModel(model: LegacyEventModel) {
        this.model = model;

        const sessions = model.sessions;
        for(const s of sessions) {
            if(!s.time_y) s.time_y = $time(s.time);
        }
        this._sessions = sessions.filter(s => !s.cancelled).sort((a, b) => $time.compare(a.time_y, b.time_y));
    }

    duplicateModel() {
        const copy = yCopy(this.model);
        copy.names = {}
        for(const l in this.model.names) {
            copy.names[l as YLanguage] = 'Copy of ' + this.model.names[l as YLanguage];
        }
        copy.sessions = [];
        copy.videos = [];
        copy.id = '' as YObjectId;
        copy.owner = '' as YObjectId;
        copy.published = false;

        return copy;
    }

    getSession(id: YMaybeObjectId) {
        return this._sessions.find(s => s.id == id);
    }

    getAnySession(id: YMaybeObjectId) {
        return this.model.sessions.find(s => s.id == id);
    }

    getVideo(id: YMaybeObjectId) {
        return (this.model.videos || []).find(v => v.id == id);
    }

    hasSessionsBetween(from: YTime | undefined, to: YTime | undefined) {
        if(!from && !to) return true;

        for(const s of this._sessions) {
            if(s.time_y!.inRange(from, to))
                return true;
        }

        return false;
    }

    hasDropInSessions() {
        for(const s of this._sessions) {
            if(s.drop_in)
                return true;
        }

        return false;
    }

    hasTag(tag_id: YObjectId) {
        return (this.model.tags || []).includes(tag_id);
    }

    matchesQuery(query: string[]) {
        if(!query.length) return false;

        if(!this._search) {
            const model = this.model;
            let search = ' ' + this.name;
            const venue = c_venues.maybe($id.objectIdToId(model.venue as YObjectId));
            if(venue)
                search += ' ' + venue.name;
            const now = $time.now();
            const we_start = $time.make(now.year, now.month, now.day_of_month - 1 + (7 - now.day_of_week) % 7);
            const we_end = we_start.add(2, $time.day);
            const today = now.formatIn('EEEE d MMMM', 'Europe/Stockholm');
            const tomorrow = now.add(1, $time.day).formatIn('EEEE d MMMM', 'Europe/Stockholm');
            for(const s of this._sessions) {
                if(s.time_y.add(-s.sales_end, $time.hour).before(now)) continue;
                const date = s.time_y.formatIn('EEEE d MMMM', 'Europe/Stockholm');
                search += ' ' + date;
                if(date === today)
                    search += ' idag';
                if(date === tomorrow)
                    search += ' imorgon';
                if(s.time_y.after(we_start) && s.time_y.before(we_end))
                    search += ' helgen';
            }
            for(const t of(model.tags || [])) {
                const tag = c_event_tags.maybe($id.objectIdToId(t));
                if(tag)
                    search += ' ' + cEventTagName(tag);
            }

            search += ' ';

            this._search = search.toLocaleLowerCase();
        }

        const search = this._search;

        for(const w of query) {
            if(!search.includes(w))
                return false;
        }

        return true;
    }

    get name() {
        return yGetI18N(this.model.names || {});
        // return yGetI18N(this.model.names);
    }

    get description() {
        return yGetI18N(this.model.descriptions || {});
    }

    get duration_text() {
        if(!this.hasDropInSessions()) {
            const length = this.model.length
            if(length > 0) {
                const hours = Math.floor(length / 60)
                const minutes = length % 60;
                return (hours ? hours + ' ' + $i18n.tl.t_event_duration_h + ' ' : '') + (minutes ? minutes + ' ' + $i18n.tl.t_event_duration_m : '');
            }
        }

        return;
    }

    get sessions() {
        return this._sessions;
    }

    get active_sessions() {
        return this._sessions;
    }

    get has_sessions() {
        return this._sessions.length;
    }

    get has_videos() {
        return this.model.videos && this.model.videos.length;
    }

    get next_session() {
        const now = $time.now();
        for(const s of this._sessions) {
            if(s.time_y.after(now))
                return s;
        }

        return;
    }

    get next_or_first_time() {
        const next = this.next_session;
        if(next) return next.time_y;
        return this._sessions.length ? this._sessions[0].time_y : undefined;
    }

    get first_time_and_more() {
        const sessions = this._sessions;
        if(!sessions.length)
            return '-';
        else if(sessions.length > 1)
            return sessions[0].time_y.format('d MMM yyyy HH:mm') + ' + fler datum';
        else
            return sessions[0].time_y.format('d MMM yyyy HH:mm');
    }

    get bookable_sessions() {
        return this._sessions.filter(s => legacyIsSessionBookable(s));
    }

    get past_sessions() {
        return this._sessions.filter(legacyIsSessionPast);
    }

    get next_bookable_session() {
        return this._sessions.find(s => legacyIsSessionBookable(s));
    }

    getBookableSessionAfter(when: YTime) {
        let bookable: LegacyEventSession | undefined;
        for(const s of this._sessions) {
            if(legacyIsSessionBookable(s)) {
                if(s.time_y.after(when))
                    return s;
                else if(!bookable)
                    bookable = s;
            }
        }
        return bookable;
    }

    get next_future_session() {
        return this._sessions.find(s => legacyIsSessionBookable(s, true));
    }

    get next_livestream() {
        return (this.model.videos || []).find(v => this.isFutureLivestream(v))
    }

    get total_bookings() {
        return this._sessions.reduce((n, s) => n + s.amount_booked, 0);
    }

    getNextSessionTextOrSeveral() {
        const bookable = this.bookable_sessions;
        if(bookable.length) {
            if(bookable.length > 1)
                return $i18n.tl.several_dates;

            const text = bookable[0].time_y.formatIn(bookable[0].drop_in ? 'd MMM' : 'd MMM, HH:mm', 'Europe/Stockholm');
            return text;
        }

        const model = this.model;
        if(model.videos && model.videos.length && this.isVideoAvailable(model.videos[0])) {
            const time = $time(model.videos[0].date);
            if(time.is_future) {
                return $i18n.tl.t_video_starts + ' ' + time.format('d MMM, HH:mm');
            } else if(time.add(model.length, $time.minute).is_future) {
                return $i18n.tl.t_video_live_now;
            }
        }

        return;
    }

    getNextSessionText(after?: YTime) {
        let bookable = this.bookable_sessions;
        if(bookable.length) {
            if(after) {
                const filtered_bookable = bookable.filter(s => s.time_y.after(after));
                if(filtered_bookable.length)
                    bookable = filtered_bookable;
            }
            if(bookable.length > 1)
                return $i18n.tl.several_dates;
            const text = bookable[0].time_y.formatIn(bookable[0].drop_in ? 'd MMM' : 'd MMM, HH:mm', 'Europe/Stockholm');
            return text;
        }

        const model = this.model;
        if(model.videos && model.videos.length && this.isVideoAvailable(model.videos[0])) {
            const time = $time(model.videos[0].date);
            if(time.is_future) {
                return $i18n.tl.t_video_starts + ' ' + time.format('d MMM, HH:mm');
            } else if(time.add(model.length, $time.minute).is_future) {
                return $i18n.tl.t_video_live_now;
            }
        }

        return;
    }

    get track_properties() {
        const model = this.model;
        const properties: { [index: string]: any } = { event_id: model.id, event_name: this.name, event_keyword: model.keywords };
        const city = c_cities.maybe($id.objectIdToId(model.city));
        if(city)
            properties.event_city = city.name;
        const venue = c_venues.maybe($id.objectIdToId(model.venue as YObjectId))
        if(venue)
            properties.venue = venue.name;
        const producer = c_producers.maybe(model.producer)
        if(producer)
            properties.producer = producer.name;
        const genre = this.genre_tag;
        if(genre)
            properties.genre = genre.name;
        if(model.videos && model.videos.length && model.videos[0].stream)
            properties.is_livestream = true;
        return properties;
    }

    isVideoAvailable(video: LegacyEventVideo) {
        if(video.gone || video.cancelled) return false;
        const expires = $time(video.expires);
        if(expires.n && expires.is_past) return false;

        return true;
    }

    isFutureLivestream(video: LegacyEventVideo) {
        if(!this.isVideoAvailable(video)) return false;
        const date = $time(video.date);
        return date.n && date.add(this.model.length, $time.minute).is_future;
    }


    getUnbookingEnd(session: LegacyEventSession) {
        return session.time_y.add(-24, $time.hour);
    }

    getBookingEnd(session: LegacyEventSession) {
        return session.time_y.add(-session.sales_end, $time.hour);
    }

    getEndTime(session: LegacyEventSession) {
        if(session.drop_in)
            return $time(session.end_time);
        else
            return session.time_y.add(this.model.length, $time.minute);
    }

    get screen_location() {
        return ['event', c_cities.maybe($id.objectIdToId(this.model.city))!.slug, this.model.slug];
    }

    get player_location() {
        return ['event', c_cities.maybe($id.objectIdToId(this.model.city))!.slug, this.model.slug, 'player'];
    }

    get page_url(): string {
        return 'https://abundolive.se/' + this.screen_location.join('/');
    }

    get genre_tag() {
        for(const t of this.model.tags || []) {
            const tag = c_event_tags.maybe($id.objectIdToId(t));
            if(tag?.is_genre)
                return tag;
        }

        return;
    }

    get genre_tags() {
        const tags: CEventTag[] = [];

        for(const t of this.model.tags || []) {
            const tag = c_event_tags.maybe($id.objectIdToId(t));
            if(tag?.is_genre)
                tags.push(tag);
        }

        return tags;
    }

    get rating_score() {
        const up = this.model.ratings_up;
        const total = up + this.model.ratings_down;
        if(!total) return -1;
        return up / total;
    }

    static filterActive(e: LegacyEvent) {
        return !!e.next_bookable_session;
    }

    static filterFuture(e: LegacyEvent) {
        return !!e.next_future_session;
    }

    static filterUpcoming(e: LegacyEvent) {
        return !!e.next_bookable_session || !!e.next_livestream;
    }

    static filterPast(e: LegacyEvent) {
        const now = $time.now();
        for(const s of e._sessions) {
            if(now.add(s.sales_end, $time.hour).before(s.time_y))
                return false;
        }

        return true;
    }
}

export const legacy_events = makeDataStore('events', 'event', (m: LegacyEventModel) => new LegacyEvent(m), (o, m) => { o.updateModel(m) });

const tz = 'Europe/Stockholm';

export function legacyPrettySessionTime(session: LegacyEventSession, month_part: string = 'MMM yyyy') {
    const s = session.time_y;
    if(!session.drop_in) return s.formatIn('EEE d MMM yyyy \u00B7 HH:mm', tz);
    const e = $time(session.end_time);
    if(!e.n)
        return s.formatIn('EEE d ' + month_part, tz);

    if(s.year !== e.year || s.month !== e.month || s.day_of_month !== e.day_of_month) {
        return s.format('d ' + month_part + ' \u00B7 HH:mm') + ' - ' + e.format('d MMM yyyy HH:mm');
    } else {
        return s.format('EEE d ' + month_part + ' \u00B7 HH:mm') + ' - ' + e.format('HH:mm');
    }
}

interface LegacyOpenEventsResponse {
    events: LegacyEventModel[];
    new_venues: CVenueSceneView[];
    producers: CProducer[];
    new_cities: CCity[];
}

const getOpenEvents = $fn.lazy1((sub_path: string) => {
    return $http.GET<LegacyOpenEventsResponse>('/api/v1/open_events/' + sub_path).then(data => {
        data.new_cities.forEach(c_cities.register);
        data.new_venues.forEach(c_venues.register);
        data.producers.forEach(c_producers.register);
        return data.events.map(legacy_events.register);
    });
})

export function legacyEventsGetPast(days: number, city?: YId) {
    return getOpenEvents('past?days=' + days + (city ? '&city=' + city : ''));
}

export function legacyEventsGetPastForYear(year: string, city?: YId) {
    return getOpenEvents('past?year=' + year + (city ? '&city=' + city : ''));
}

export function legacyEventsGetProducerBySlug(slug: string) {
    return getOpenEvents('producer_slug/' + slug).then(events => {
        const producer = c_producers.all.find(p => p.slug == slug)!;
        return { producer: producer, events: events };
    });
}

export function legacyEventsGetActiveByTagId(tag_id: YId) {
    return getOpenEvents('event_tag_active/' + tag_id);
}

export function legacyEventsGetVideoByTagId(tag_id: YId) {
    return getOpenEvents('event_tag_video/' + tag_id);
}

export function legacyEventsByCity(city: YId = abGetDefaultCity().id) {
    return getOpenEvents('city/' + city);
}

export function legacyEventsGetFeatured() {
    return getOpenEvents('featured');
}

export function legacyEventsGetOnline() {
    return getOpenEvents('online');
}

export function legacyEventsGetVenueByCityAndSlug(city_slug: string, slug: string) {
    return getOpenEvents('venue_slug/' + city_slug + '/' + slug).then(events => {
        const city_id = cCityBySlug(city_slug)!.id;
        const venue = c_venues.all.find(p => p.city_id == city_id && p.slug == slug)!;
        return { venue: venue, events: events };
    });
}

interface LegacyOpenEventResponse {
    event: LegacyEventModel;
    new_venue: CVenueSceneView;
    producer: CProducer;
    new_cities: CCity[];
}

function getOpenEvent(sub_path: string) {
    return $http.GET<LegacyOpenEventResponse>('/api/v1/open_event/' + sub_path).then(data => {
        c_cities.init(data.new_cities);
        c_venues.register(data.new_venue);
        c_producers.register(data.producer)
        return legacy_events.register(data.event);
    });
}

export function legacyEventByID(id: YObjectId) {
    const event = legacy_events.maybe(id)

    if(event)
        return Promise.resolve(event);

    return getOpenEvent('id/' + id);
}

export function legacyEventByCityAndSlug(city_slug: string, slug: string) {
    const city = cCityBySlug(city_slug);

    if(city) {
        for(const e of legacy_events.all) {
            if($id.objectIdToId(e.model.city) == city.id && e.model.slug == slug)
                return Promise.resolve(e);
        }
    }

    return getOpenEvent('slug/' + city_slug + '/' + slug);
}

export interface LegacyAvailability {
    id: YObjectId;
    bookable: boolean;
    already: boolean;
    standby: boolean;
    can_book: boolean;
    can_standby: boolean;
    reason: LegacyCantBookReason;
    max_tickets: number;
    max_book: number;
    max_book_free: number;
    max_standby: number;
    dots: number;
    price: YPaymentAmount;
    instructions: YI18NString;
    days_until_loyalty: number;
}

export function legacyEventGetAvailability(event_id: YId | YObjectId) {
    return $http.GET<LegacyAvailability[]>(`/api/v1/event/${event_id}/availability`);
}


let last_reviews_id: YObjectId | undefined;
let last_reviews: CReviewHomeView[];

export function legacyGetEventReviews(id: YObjectId) {
    if(last_reviews_id == id)
        return Promise.resolve(last_reviews);

    return $http.GET<CReviewHomeView[]>('/api/v1/event/' + id + '/reviews').then(reviews => {
        last_reviews_id = id;
        last_reviews = reviews;
        return reviews;
    });
}

